import React, { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { publicApi } from '../services/api';
import { Panel, H2, ButtonPrimary, ButtonGhost } from './public';

/**
 * A change to a quote the customer already accepted — everything they see about it.
 *
 * The owner, Oct 6 2026: "can you allow me to make change orders on bids? So if
 * we are going to change a bid, but it's already accepted, click change order
 * so I can change something on the bid and then resend."
 *
 * This sits on the quote's own link, above "You're on the schedule". It lives
 * in its own file because the quote page (pages/QuotePublic) is being
 * redesigned by someone else: that page imports this and renders it, nothing
 * more. Every figure and every sentence about the change comes from the server
 * (services/changeOrderSend), so the page, the text and the office never
 * disagree; this file only lays them out and sends the answer back.
 *
 * What the customer can do: approve it (a six-digit code we text or email
 * proves it is them, then their name), or keep the quote the way it was.
 * Nothing changes until they approve, and approving charges nothing.
 *
 * A change that only lowers the price (the server says so: lower_only) is
 * accepted with ONE TAP — their name, pre-filled, and "Accept this change".
 * No code is sent or asked for. The owner, Oct 6 2026: "make them accept, not
 * a new code". The server decides which kind a change is from the change it
 * stored; this page only lays out the one it was told.
 * They read "quote" and "change" — never "bid" or "change order".
 */

type Row = { op: 'add' | 'remove' | 'change'; words: string };
type Change = {
  number: number; reason: string; rows: Row[];
  totals: { was: string; change: string; now: string; stays: string | null };
  deposit_words: string | null; until_words: string; no_charge_words: string;
  shown_hash: string; original: { heading: string; lines: { description: string; price: string }[] };
  // The whole quote as it will be once this change is in — every line, the touched ones marked.
  after?: { description: string; price: string; mark: 'add' | 'change' | 'remove' | null; was: string | null }[];
  name: string; needs_code: boolean;
  // It only lowers the price: one tap accepts it. button_words is "Accept this change" then, else "Approve this change".
  lower_only?: boolean; button_words?: string;
};
type Notice = { status: string; heading: string; body: string };
type ChangeState = {
  change?: Change | null; change_updating?: boolean; change_notice?: Notice | null;
  changes_done?: { number: number; words: string }[];
  // The whole quote as it stands now, once a change has been made to it.
  quote_now?: { description: string; price: string }[];
};
type Verify = { to: string; via: string; options?: { email?: string | null; sms?: string | null }; practice_code?: string | null };

const OP_LABEL: Record<Row['op'], string> = { add: 'Added', remove: 'Removed', change: 'Changed' };
const OP_SKIN: Record<Row['op'], string> = {
  add: 'bg-emerald-100 text-emerald-900',
  remove: 'bg-gray-200 text-gray-800',
  change: 'bg-brand-100 text-brand-800',
};
const FIELD = 'w-full border-2 border-brand-200 rounded-2xl px-4 min-h-[56px] text-[17px] focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100';
const QUIET = 'press text-[14px] font-bold text-brand-700 underline min-h-[48px] disabled:opacity-50';

/** The server's row sentence ("Added · Mulch, 3 yards · +$285.00") without its first word — that is drawn as a chip. */
const afterLabel = (r: Row) => (r.words.startsWith(`${OP_LABEL[r.op]} · `) ? r.words.slice(OP_LABEL[r.op].length + 3) : r.words);

export default function ChangeOrderPublic({ token, title, data, onChanged }: {
  token?: string; title?: string | null; data?: ChangeState | null; onChanged?: () => void;
}) {
  // What the server last told THIS tab — after an answer, or when it says the change was updated since the page
  // opened. A fresh load of the page replaces it.
  const [latest, setLatest] = useState<ChangeState | null>(null);
  const [stale, setStale] = useState<string | null>(null);
  const [answered, setAnswered] = useState<Notice | null>(null);
  const [step, setStep] = useState<'idle' | 'approve' | 'keep'>('idle');
  const [verify, setVerify] = useState<Verify | null>(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  useEffect(() => { setLatest(null); }, [data]);

  const view: ChangeState = latest || data || {};
  const change = view.change || null;
  // Their name as they signed the quote the first time — theirs to correct.
  const signedAs = change?.name || '';
  useEffect(() => { if (signedAs) setName(n => n || signedAs); }, [signedAs]);

  const base = `/quotes/public/${token}/change/${change?.number}`;
  const reset = () => { setStep('idle'); setVerify(null); setCode(''); setError(null); };

  /** The server says what this tab shows is no longer the change: show what it sent back, and say so. */
  function moved(d: any) {
    const st: ChangeState | null = d?.state || null;
    if (st) setLatest(st);
    // Taken back, or being edited again: the page below now says exactly that, so it is not said twice.
    const saysItself = !!st && !st.change && (!!st.change_updating || !!st.change_notice);
    setStale(saysItself ? null : d?.message || 'This was updated since you opened it — here is the latest.');
    reset();
    onChanged?.();
  }

  async function sendCode(via?: 'sms' | 'email') {
    setBusy(true); setError(null);
    try {
      const d = (await publicApi.post(`${base}/send-code`, via ? { via } : {})).data;
      setVerify({ to: d.to, via: d.via, options: d.options || {}, practice_code: d.practice_code || null });
      setCode('');
    } catch (e: any) {
      const d = e?.response?.data;
      if (e?.response?.status === 409) moved(d);
      // Nowhere to send a code: the server takes their tap and their name instead.
      else if (d?.no_contact) setVerify({ to: '', via: 'none' });
      else setError(d?.message || 'Could not send the code. Try again in a moment.');
    } finally { setBusy(false); }
  }

  function startApprove() {
    setStale(null); setError(null); setStep('approve');
    if (!change) return;
    if (change.needs_code) sendCode(); else setVerify({ to: '', via: 'none' });
  }

  async function answer(action: 'approve' | 'keep') {
    if (!change) return;
    setBusy(true); setError(null);
    try {
      const d = (await publicApi.post(`${base}/respond`, {
        action, shown_hash: change.shown_hash,
        code: action === 'approve' ? code.trim() || undefined : undefined,
        name: action === 'approve' ? name.trim() : undefined,
        note: action === 'keep' ? note.trim() || undefined : undefined,
      })).data;
      setAnswered({ status: d.status, heading: d.heading, body: d.body });
      if (d.state) setLatest(d.state);
      setStale(null); reset();
      onChanged?.();
    } catch (e: any) {
      const d = e?.response?.data;
      if (e?.response?.status === 409) moved(d);
      else if (d?.code_required && !verify && !change.lower_only) sendCode();
      else setError(d?.message || 'That did not go through. Try again.');
    } finally { setBusy(false); }
  }

  const needsCode = !!verify && verify.via !== 'none';
  const canApprove = !busy && !!verify && name.trim().length > 1 && (!needsCode || code.length === 6);
  // A change that only lowers the price: their name and one tap. No code step at all.
  const oneTap = !!change?.lower_only;
  const buttonWords = change?.button_words || (oneTap ? 'Accept this change' : 'Approve this change');
  const notice = !answered && !change ? view.change_notice || null : null;

  if (!change && !answered && !stale && !view.change_updating && !notice) return null;

  return (
    <>
      {stale && (
        <Panel tone="warn"><p className="text-[15px] font-semibold text-gray-900" role="status">{stale}</p></Panel>
      )}

      {answered && (
        <Panel tone={answered.status === 'declined' ? 'plain' : 'good'}>
          <H2>{answered.heading}</H2>
          <p className="text-[15px] text-gray-800 mt-2">{answered.body}</p>
        </Panel>
      )}

      {!change && !answered && view.change_updating && (
        <Panel tone="brand"><p className="text-[15px] text-gray-900">We are updating this change — nothing has changed.</p></Panel>
      )}

      {notice && (
        <Panel tone={notice.status === 'approved' ? 'good' : 'plain'}>
          <p className="text-[15px] text-gray-900">{notice.status === 'approved' ? `${notice.heading} — ${notice.body}` : notice.body}</p>
        </Panel>
      )}

      {change && (
        <Panel tone="brand">
          <p className="text-[13px] font-black uppercase tracking-wide text-brand-700">Change to your quote</p>
          <H2 className="mt-1">{title || 'Your quote'}</H2>
          {change.reason && <p className="text-[15px] text-gray-900 mt-2">{change.reason}</p>}

          <div className="mt-4 rounded-2xl bg-white border border-brand-200 p-4">
            <h3 className="font-black uppercase tracking-tight text-base text-gray-900 mb-2">What changed</h3>
            <ul className="space-y-3">
              {change.rows.map((r, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className={`flex-shrink-0 mt-0.5 inline-flex px-2 py-1 rounded-lg text-[12px] font-black uppercase tracking-wide ${OP_SKIN[r.op]}`}>{OP_LABEL[r.op]}</span>
                  <span className="text-[15px] text-gray-900 tabular-nums">{afterLabel(r)}</span>
                </li>
              ))}
            </ul>
            {/* The whole quote, not only the change (owner, Oct 6 2026: "show the customer the full new bid"). */}
            {(change.after || []).length > 0 && (
              <div className="mt-4 pt-3 border-t-2 border-brand-100">
                <h3 className="font-black uppercase tracking-tight text-base text-gray-900 mb-2">Your quote with this change</h3>
                <ul className="space-y-2">
                  {(change.after || []).map((l, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 text-[15px]">
                      <span className={`min-w-0 ${l.mark === 'remove' ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                        {l.description}
                        {l.mark && <span className={`ml-2 inline-flex px-1.5 py-0.5 rounded-md text-[11px] font-black uppercase tracking-wide align-middle no-underline ${OP_SKIN[l.mark]}`}>{l.mark === 'add' ? 'New' : l.mark === 'change' ? 'Changed' : 'Removed'}</span>}
                      </span>
                      <span className="flex-shrink-0 text-right tabular-nums">
                        <span className={l.mark === 'remove' ? 'text-gray-500 line-through' : 'font-semibold text-gray-900'}>{l.price}</span>
                        {l.was && <span className="block text-[13px] text-gray-500">was {l.was}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-4 pt-3 border-t-2 border-brand-100 space-y-1 tabular-nums">
              <div className="text-[15px] text-gray-800">{change.totals.was}</div>
              <div className="text-[15px] text-gray-800">{change.totals.change}</div>
              <div className="text-[19px] font-black text-gray-900">{change.totals.now}</div>
              {change.totals.stays && <div className="text-[14px] text-gray-700">{change.totals.stays}</div>}
            </div>
          </div>

          {change.deposit_words && <p className="text-[15px] text-gray-900 mt-3">{change.deposit_words}</p>}
          <p className="text-[15px] font-semibold text-gray-900 mt-3">{change.until_words}</p>

          {change.original.lines.length > 0 && (
            <div className="mt-2">
              <button type="button" onClick={() => setShowOriginal(v => !v)} aria-expanded={showOriginal}
                className="press inline-flex items-center gap-1 text-[14px] font-bold text-brand-700 underline min-h-[48px]">
                {change.original.heading}
                <ChevronDown className={`h-4 w-4 transition-transform ${showOriginal ? 'rotate-180' : ''}`} aria-hidden />
              </button>
              {showOriginal && (
                <ul className="rounded-2xl bg-white border border-brand-200 p-4 space-y-2">
                  {change.original.lines.map((l, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-3 text-[15px] text-gray-900">
                      <span>{l.description}</span><span className="tabular-nums flex-shrink-0">{l.price}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {step === 'idle' && oneTap && (
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="block text-[15px] font-bold text-gray-900 mb-1">Your full name</span>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" autoComplete="name" autoCapitalize="words"
                  className={`${FIELD} bg-white`} />
              </label>
              {error && <p className="text-[15px] font-semibold text-red-700" role="alert">{error}</p>}
              <ButtonPrimary onClick={() => { setStale(null); answer('approve'); }} disabled={busy || name.trim().length < 2}>{busy ? 'Sending' : buttonWords}</ButtonPrimary>
              <p className="text-[13px] text-gray-700 text-center">{change.no_charge_words}</p>
              <div className="text-center">
                <button type="button" onClick={() => { setStale(null); setError(null); setStep('keep'); }} disabled={busy} className={QUIET}>Keep it the way it was</button>
              </div>
            </div>
          )}

          {step === 'idle' && !oneTap && (
            <div className="mt-3">
              <ButtonPrimary onClick={startApprove} disabled={busy}>Approve this change</ButtonPrimary>
              <p className="text-[13px] text-gray-700 text-center mt-2">{change.no_charge_words}</p>
              <div className="text-center">
                <button type="button" onClick={() => { setStale(null); setError(null); setStep('keep'); }} disabled={busy} className={QUIET}>Keep it the way it was</button>
              </div>
            </div>
          )}

          {step === 'approve' && (
            <div className="mt-4 space-y-3">
              {needsCode && (
                <div>
                  <p className="text-[15px] text-gray-900">
                    We {verify!.via === 'sms' ? 'texted' : 'emailed'} a 6-digit code to <b>{verify!.to}</b>. It confirms it is really you. Codes last 15 minutes.
                  </p>
                  {verify!.practice_code && <p className="mt-2 text-[14px] font-bold text-amber-700">Practice mode. The code is {verify!.practice_code}</p>}
                  <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    inputMode="numeric" autoComplete="one-time-code" placeholder="000000" aria-label="6-digit code"
                    className="mt-3 w-full bg-white border-2 border-brand-200 rounded-2xl px-4 min-h-[64px] text-3xl tracking-[0.35em] text-center font-black focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
                  <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1">
                    <button type="button" onClick={() => sendCode(verify!.via as 'sms' | 'email')} disabled={busy} className={QUIET}>Send a new code</button>
                    {verify!.via === 'email' && verify!.options?.sms && (
                      <button type="button" onClick={() => sendCode('sms')} disabled={busy} className={QUIET}>Text it instead</button>
                    )}
                    {verify!.via === 'sms' && verify!.options?.email && (
                      <button type="button" onClick={() => sendCode('email')} disabled={busy} className={QUIET}>Email it instead</button>
                    )}
                  </div>
                </div>
              )}
              {!verify && !error && <p className="text-[15px] text-gray-900" role="status">Sending your code…</p>}
              <label className="block">
                <span className="block text-[15px] font-bold text-gray-900 mb-1">Your full name</span>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" autoComplete="name" autoCapitalize="words"
                  className={`${FIELD} bg-white`} />
              </label>
              {error && <p className="text-[15px] font-semibold text-red-700" role="alert">{error}</p>}
              <ButtonPrimary onClick={() => answer('approve')} disabled={!canApprove}>{busy ? 'Sending' : 'Approve this change'}</ButtonPrimary>
              <p className="text-[13px] text-gray-700 text-center">{change.no_charge_words}</p>
              <div className="text-center"><button type="button" onClick={reset} disabled={busy} className={QUIET}>Not yet</button></div>
            </div>
          )}

          {step === 'keep' && (
            <div className="mt-4 space-y-3">
              <label className="block">
                <span className="block text-[15px] font-bold text-gray-900 mb-1">Anything Michael should know?</span>
                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} maxLength={500}
                  className="w-full bg-white border-2 border-brand-200 rounded-2xl px-4 py-3 text-[16px] focus:outline-none focus:border-brand-600 focus:ring-4 focus:ring-brand-100" />
              </label>
              {error && <p className="text-[15px] font-semibold text-red-700" role="alert">{error}</p>}
              <ButtonGhost onClick={() => answer('keep')} disabled={busy}>{busy ? 'Sending' : 'Keep it the way it was'}</ButtonGhost>
              <div className="text-center"><button type="button" onClick={reset} disabled={busy} className={QUIET}>Go back</button></div>
            </div>
          )}
        </Panel>
      )}
    </>
  );
}

/** Under the thank-you: every change already made to the quote — "Oct 7 · adds $445.00 · new total $1,685.00". */
export function ChangesDone({ data }: { data?: ChangeState | null }) {
  const done = data?.changes_done || [];
  if (!done.length) return null;
  return (
    <Panel title="Changes to your quote">
      <ul className="space-y-2">
        {done.map(c => <li key={c.number} className="text-[15px] text-gray-900 tabular-nums">{c.words}</li>)}
      </ul>
      {(data?.quote_now || []).length > 0 && (
        <div className="mt-4 pt-3 border-t-2 border-brand-100">
          <h3 className="font-black uppercase tracking-tight text-base text-gray-900 mb-2">Your quote now</h3>
          <ul className="space-y-2">
            {(data?.quote_now || []).map((l, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-[15px] text-gray-900">
                <span className="min-w-0">{l.description}</span><span className="flex-shrink-0 font-semibold tabular-nums">{l.price}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}
